import { useState, type ChangeEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, Badge } from '@besiktning/ui'
import { Briefcase, ChevronRight, X } from 'lucide-react'
import { garageApi, carCareApi, formatCurrency, formatInTz } from '@besiktning/ui'
import { useAuthStore } from '../store/authStore'
import { t } from '../i18n'
import type { Booking, CarCareBooking } from '@besiktning/ui'

type FilterTab = 'all' | 'upcoming' | 'completed' | 'cancelled'

const TABS: { key: FilterTab; labelKey: string }[] = [
  { key: 'all', labelKey: 'tabAll' },
  { key: 'upcoming', labelKey: 'tabUpcoming' },
  { key: 'completed', labelKey: 'tabCompleted' },
  { key: 'cancelled', labelKey: 'tabCancelled' },
]

const STATUS_LABEL_KEYS: Record<string, string> = {
  pending_payment: 'statusPendingPayment',
  pending: 'statusPending',
  confirmed: 'statusConfirmed',
  completed: 'statusCompleted',
  cancelled: 'statusCancelled',
  pending_cancellation: 'statusPendingCancellation',
}

const STATUS_VARIANTS: Record<string, 'default' | 'success' | 'warning' | 'danger'> = {
  pending_payment: 'warning',
  pending: 'warning',
  confirmed: 'default',
  completed: 'success',
  cancelled: 'danger',
  pending_cancellation: 'warning',
}

function filterBookings(bookings: (Booking | CarCareBooking)[], tab: FilterTab) {
  const now = new Date()
  switch (tab) {
    case 'upcoming':
      return bookings.filter(
        (b) => !['completed', 'cancelled'].includes(b.status) && new Date(b.scheduled_at ?? '') >= now
      )
    case 'completed':
      return bookings.filter((b) => b.status === 'completed')
    case 'cancelled':
      return bookings.filter((b) => ['cancelled', 'pending_cancellation'].includes(b.status))
    default:
      return bookings
  }
}

type Filters = { customer: string; date: string; vehicle: string }

function vehicleOf(booking: Booking | CarCareBooking) {
  return 'vehicle' in booking && booking.vehicle
    ? booking.vehicle
    : (booking as Booking).quote?.inspection_request?.vehicle
}

// Client-side: the list endpoint already returns every job.
function matchesFilters(booking: Booking | CarCareBooking, { customer, date, vehicle }: Filters) {
  const has = (haystack: (string | null | undefined)[], needle: string) =>
    haystack.filter(Boolean).join(' ').toLowerCase().includes(needle.trim().toLowerCase())

  if (customer && !has([booking.user?.name, booking.user?.phone, booking.user?.email], customer)) return false
  // scheduled_at carries the brand's UTC offset, so its date part is already the local day.
  if (date && booking.scheduled_at?.slice(0, 10) !== date) return false
  const v = vehicleOf(booking)
  if (vehicle && !has([v?.reg_number, v?.make, v?.model], vehicle)) return false
  return true
}

function BookingRow({
  booking,
  isCarCare,
  onClick,
}: {
  booking: Booking | CarCareBooking
  isCarCare: boolean
  onClick: () => void
}) {
  const ccBooking = isCarCare ? (booking as CarCareBooking) : null
  const gBooking = !isCarCare ? (booking as Booking) : null

  const refLabel = isCarCare
    ? (ccBooking?.booking_reference ?? `#${booking.id}`)
    : `#${booking.id}`

  const serviceName = isCarCare
    ? ccBooking!.car_care_service.name
    : gBooking?.title || t('jobs.defaultBookingTitle')

  const customerName = booking.user?.name || '—'

  const v = vehicleOf(booking)
  const vehicleLabel = [v?.reg_number, [v?.make, v?.model].filter(Boolean).join(' ')].filter(Boolean).join(' • ') || '—'

  const dateLabel = booking.scheduled_at
    ? formatInTz(booking.scheduled_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—'

  const price = isCarCare ? ccBooking?.price : (gBooking as any)?.quote?.price

  const paymentStatus = (booking as any).payment_status
  const isPaid = paymentStatus === 'paid'
  const displayStatus = (booking.status === 'pending_payment' && isPaid)
    ? (isCarCare ? 'pending' : 'confirmed')
    : booking.status

  return (
    <tr
      onClick={onClick}
      className="hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex h-8 w-8 rounded-full bg-blue-100 items-center justify-center flex-shrink-0">
            <Briefcase className="h-4 w-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{serviceName}</p>
            <p className="text-xs text-gray-400">{refLabel}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-700 truncate">{customerName}</td>
      <td className="hidden sm:table-cell px-4 py-3 text-sm text-gray-700 truncate" title={vehicleLabel}>{vehicleLabel}</td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">{dateLabel}</td>
      <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-700 text-right">
        {price != null ? formatCurrency(price) : '—'}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <Badge variant={STATUS_VARIANTS[displayStatus] ?? 'default'}>
            {STATUS_LABEL_KEYS[displayStatus] ? t(`jobs.${STATUS_LABEL_KEYS[displayStatus]}`) : displayStatus}
          </Badge>
          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
        </div>
      </td>
    </tr>
  )
}

export default function Jobs() {
  const { currentMembership } = useAuthStore()
  const isCarCare = currentMembership?.type === 'car_care'
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [filters, setFilters] = useState<Filters>({ customer: '', date: '', vehicle: '' })
  const setFilter = (key: keyof Filters) => (e: ChangeEvent<HTMLInputElement>) =>
    setFilters((f) => ({ ...f, [key]: e.target.value }))
  const hasFilters = Object.values(filters).some(Boolean)

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['jobs', isCarCare ? 'car_care' : 'garage'],
    queryFn: () => (isCarCare ? carCareApi.getBookings() : garageApi.getBookings()),
    refetchInterval: 30_000,
  })

  const matching = (bookings as (Booking | CarCareBooking)[]).filter((b) => matchesFilters(b, filters))

  const filtered = filterBookings(matching, activeTab)
    .slice()
    .sort((a, b) => {
      const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0
      const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0
      return tb - ta
    })

  const counts: Record<FilterTab, number> = {
    all: matching.length,
    upcoming: filterBookings(matching, 'upcoming').length,
    completed: filterBookings(matching, 'completed').length,
    cancelled: filterBookings(matching, 'cancelled').length,
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('jobs.title')}</h1>
        <p className="text-gray-600">{t('jobs.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t(`jobs.${tab.labelKey}`)}
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex flex-col text-xs font-medium text-gray-500">
          {t('jobs.colCustomer')}
          <input type="search" value={filters.customer} onChange={setFilter('customer')}
            placeholder={t('jobs.filterCustomerPlaceholder')}
            className="mt-1 h-10 w-52 rounded-lg border border-gray-300 px-3 text-sm text-gray-900" />
        </label>
        <label className="flex flex-col text-xs font-medium text-gray-500">
          {t('jobs.colDate')}
          <input type="date" value={filters.date} onChange={setFilter('date')}
            className="mt-1 h-10 rounded-lg border border-gray-300 px-3 text-sm text-gray-900" />
        </label>
        <label className="flex flex-col text-xs font-medium text-gray-500">
          {t('jobs.colVehicle')}
          <input type="search" value={filters.vehicle} onChange={setFilter('vehicle')}
            placeholder={t('jobs.filterVehiclePlaceholder')}
            className="mt-1 h-10 w-44 rounded-lg border border-gray-300 px-3 text-sm text-gray-900" />
        </label>
        {hasFilters && (
          <button onClick={() => setFilters({ customer: '', date: '', vehicle: '' })}
            className="h-10 inline-flex items-center gap-1 px-3 text-sm text-gray-600 hover:text-gray-900">
            <X className="h-4 w-4" /> {t('jobs.clearFilters')}
          </button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Briefcase className="h-12 w-12 mb-3 text-gray-200" />
              <p className="text-sm">{t('jobs.empty')}</p>
            </div>
          ) : (
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('jobs.colJob')}
                  </th>
                  <th className="w-[22%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('jobs.colCustomer')}
                  </th>
                  <th className="hidden sm:table-cell w-[24%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('jobs.colVehicle')}
                  </th>
                  <th className="hidden md:table-cell w-36 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('jobs.colDate')}
                  </th>
                  <th className="hidden lg:table-cell w-28 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('jobs.colPrice')}
                  </th>
                  <th className="w-44 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('jobs.colStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((booking) => (
                  <BookingRow
                    key={booking.id}
                    booking={booking}
                    isCarCare={isCarCare}
                    onClick={() => navigate(`/jobs/${booking.id}`)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
